/**
 * Security configuration and constants
 */

// Rate limiting configuration
export const RATE_LIMITS = {
  CONTACT_FORM: 5, // 5 submissions per hour
  API_REQUESTS: 100, // 100 requests per minute
  LOGIN_ATTEMPTS: 3 // 3 login attempts per 15 minutes
} as const;

// Input validation limits
export const INPUT_LIMITS = {
  MAX_NAME_LENGTH: 100,
  MAX_EMAIL_LENGTH: 254,
  MAX_MESSAGE_LENGTH: 2000,
  MAX_COMPANY_LENGTH: 200,
  MIN_MESSAGE_LENGTH: 10
} as const;

// Allowed domains for external links
export const ALLOWED_DOMAINS = [
  'nexx.net',
  'ncolo.net',
  'tally.so',
  'tidycal.com',
  'wa.me',
  'player.vimeo.com'
] as const;

// HSTS Configuration for staged rollout
export const HSTS_CONFIG = {
  TESTING: 'max-age=300; includeSubDomains; preload', // 5 minutes
  SHORT_TERM: 'max-age=86400; includeSubDomains; preload', // 24 hours
  MEDIUM_TERM: 'max-age=2592000; includeSubDomains; preload', // 30 days
  PRODUCTION: 'max-age=31536000; includeSubDomains; preload' // 1 year
} as const;

// Cross-Origin-Opener-Policy (COOP) Configuration
export const COOP_CONFIG = {
  STRICT: 'same-origin', // Maximum isolation
  BALANCED: 'same-origin-allow-popups', // Recommended for sites with third-party integrations
  UNSAFE: 'unsafe-none' // No isolation (not recommended)
} as const;

// Development CSP directives (more permissive for Vite HMR)
export const createDevCSPDirectives = (nonce: string) => ({
  defaultSrc: ["'self'"],
  scriptSrc: [
    "'self'",
    `'nonce-${nonce}'`,
    "'unsafe-inline'", // Allow inline scripts for dev
    "'unsafe-eval'", // Allow eval for Vite HMR
    "https://tally.so",
    "https://embed.tawk.to",
    "https://asset-tidycal.b-cdn.net",
    "https://www.googletagmanager.com"
  ],
  styleSrc: [
    "'self'",
    "'unsafe-inline'",
    "https://fonts.googleapis.com"
  ],
  imgSrc: [
    "'self'",
    "data:",
    "https:"
  ],
  connectSrc: [
    "'self'",
    "https:",
    "ws:", // Allow WebSocket connections for Vite HMR
    "wss://va.tawk.to",
    "wss://*.tawk.to"
  ],
  frameSrc: [
    "https://player.vimeo.com",
    "https://tally.so",
    "https://tawk.to"
  ],
  frameAncestors: ["'self'"], // Clickjacking protection - only allow same origin
  fontSrc: [
    "'self'",
    "data:",
    "https://fonts.gstatic.com"
  ],
  objectSrc: ["'none'"],
  baseUri: ["'self'"],
  formAction: [
    "'self'",
    "https://tally.so"
  ]
});

// Content Security Policy directives with nonce support (production)
export const createCSPDirectives = (nonce: string) => ({
  defaultSrc: ["'self'"],
  scriptSrc: [
    "'self'",
    `'nonce-${nonce}'`,
    "'strict-dynamic'",
    "https://tally.so",
    "https://embed.tawk.to",
    "https://asset-tidycal.b-cdn.net",
    "https://www.googletagmanager.com"
  ],
  styleSrc: [
    "'self'",
    "'unsafe-inline'", // Still needed for Tailwind CSS
    "https://fonts.googleapis.com"
  ],
  imgSrc: [
    "'self'",
    "data:",
    "https:"
  ],
  connectSrc: [
    "'self'",
    "https:",
    "wss://va.tawk.to",
    "wss://*.tawk.to"
  ],
  frameSrc: [
    "https://player.vimeo.com",
    "https://tally.so",
    "https://tawk.to"
  ],
  frameAncestors: ["'self'"], // Clickjacking protection - only allow same origin
  fontSrc: [
    "'self'",
    "data:",
    "https://fonts.gstatic.com"
  ],
  objectSrc: ["'none'"],
  baseUri: ["'self'"],
  formAction: [
    "'self'",
    "https://tally.so"
  ]
});

// Generate CSP string from directives with environment awareness
export const generateCSPString = (nonce: string, isDev: boolean = false) => {
  const directives = isDev ? createDevCSPDirectives(nonce) : createCSPDirectives(nonce);
  return Object.entries(directives)
    .map(([directive, sources]) => 
      `${directive.replace(/([A-Z])/g, '-$1').toLowerCase()} ${sources.join(' ')}`
    )
    .join('; ');
};

// Security headers configuration (for meta tags - server headers preferred)
export const SECURITY_HEADERS = {
  'X-Content-Type-Options': 'nosniff',
  'X-Frame-Options': 'SAMEORIGIN',
  'X-XSS-Protection': '1; mode=block',
  'Referrer-Policy': 'strict-origin-when-cross-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
  'Cross-Origin-Opener-Policy': COOP_CONFIG.BALANCED
} as const;

// Security header validation
export const validateSecurityHeaders = () => {
  const warnings: string[] = [];
  
  // Note: HSTS cannot be effectively implemented via meta tags
  warnings.push('HSTS headers should be configured at server level for maximum effectiveness');
  
  return {
    hasWarnings: warnings.length > 0,
    warnings
  };
};
